// useAuth.ts
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { pb } from "../../providers/pocketbase/pocketbase";
import { adminTrainingRepository } from "../../providers/repositories/adminTrainingRepository";
import { appRoutes, isAuthRoute } from "../../router";
import { useAdminStore } from "../store/adminStore";
import { useTrainingStore } from "../store/trainingStore";
import { trainingRepository } from "../../providers/repositories/trainingRepository";

const TRAINING_PATH = (id: string | number) => `/topic/${id}`;

function isAuthenticatedAdmin(): boolean {
  return pb.authStore.isValid && pb.authStore.record?.collectionName === "admins";
}

export function useAuth({
  onSignedIn,
  onInitialSession,
}: {
  onSignedIn?: () => Promise<void>;
  onInitialSession?: () => Promise<void>;
} = {}) {
  const navigate = useNavigate();
  const initialCheckDone = useRef(false);
  const onSignedInRef = useRef(onSignedIn);
  const onInitialSessionRef = useRef(onInitialSession);

  useEffect(() => {
    onSignedInRef.current = onSignedIn;
    onInitialSessionRef.current = onInitialSession;
  }, [onSignedIn, onInitialSession]);

  useEffect(() => {
    const applyFilterAndRedirect = async () => {
      const { setTrainings } = useTrainingStore.getState();
      const { setAdminTrainings, reset: resetAdminStore } =
        useAdminStore.getState();
      const record = pb.authStore.record;

      if (!record?.id) {
        resetAdminStore();
        return;
      }

      const adminTrainings = await adminTrainingRepository.getByAdminId(
        record.id,
      );
      setAdminTrainings(adminTrainings);

      const trainingIds = adminTrainings.map((entry) => entry.trainingId);
      if (trainingIds.length > 0) {
        const allTrainings = await trainingRepository.getAll();
        const filtered = allTrainings.filter((training) =>
          trainingIds.includes(String(training.id)),
        );
        setTrainings(filtered);

        const pathname = window.location.pathname;
        if (["/", ""].includes(pathname) && filtered.length > 0) {
          navigate(TRAINING_PATH(filtered[0].id));
        } else if (isAuthRoute()) {
          navigate(
            filtered.length > 0
              ? TRAINING_PATH(filtered[0].id)
              : appRoutes.home,
          );
        }
      } else {
        setTrainings([]);
        if (!isAuthRoute()) {
          navigate(appRoutes.signin);
        }
      }
    };

    const checkAuth = async () => {
      if (isAuthenticatedAdmin()) {
        if (!initialCheckDone.current) {
          initialCheckDone.current = true;
          await onInitialSessionRef.current?.();
        }
        await applyFilterAndRedirect();
        await onSignedInRef.current?.();
      } else if (!isAuthRoute()) {
        navigate(appRoutes.signin);
      }
    };

    checkAuth();

    const removeAuthListener = pb.authStore.onChange((token) => {
      if (token) {
        if (isAuthenticatedAdmin()) {
          applyFilterAndRedirect();
          onSignedInRef.current?.();
        }
      } else {
        initialCheckDone.current = false;
        useAdminStore.getState().reset();
        navigate(appRoutes.signin);
      }
    });

    return () => {
      removeAuthListener();
    };
  }, [navigate]);
}
